import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { Download, Package, Calendar, FileText, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [purchases, setPurchases] = useState(null);
  const [downloads, setDownloads] = useState(null);
  const [looked, setLooked] = useState(false);

  const handleFindPurchases = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const [purchasesRes, downloadsRes] = await Promise.all([
        fetch(`${API_URL}/api/payments/purchases/${encodeURIComponent(email)}`),
        fetch(`${API_URL}/api/downloads/verify/${encodeURIComponent(email)}`)
      ]);
      
      const purchasesData = await purchasesRes.json();
      const downloadsData = await downloadsRes.json();
      
      setPurchases(purchasesData.purchases || []);
      setDownloads(downloadsData.downloads || []);
      setLooked(true);
      
      if (purchasesData.purchases && purchasesData.purchases.length > 0) {
        toast({
          title: 'Purchases Found!',
          description: `Found ${purchasesData.purchases.length} purchase(s). Your downloads are ready.`,
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast({
        title: 'Error',
        description: 'Failed to look up purchases. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = (productId) => {
    window.open(`${API_URL}/api/downloads/file/${productId}?email=${encodeURIComponent(email)}`, '_blank');
  };

  return (
    <>
      <div className="min-h-screen bg-[#F5F1E8]">
        <SimpleHeader />
        
        <main className="pt-24 pb-20 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <img src="/images/spendless-logo.png" alt="SpendLess" className="w-16 h-16 rounded-full shadow-md mx-auto mb-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-4" data-testid="access-page-title">
                My Guides
              </h1>
              <p className="text-[#5A5046] text-lg">
                Access your purchased SpendLess guides and download your PDFs
              </p>
            </div>
            
            <Card className="bg-white border-none shadow-lg p-10" data-testid="access-lookup-card">
              <form onSubmit={handleFindPurchases} className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-[#2C1810] mb-2 block">Enter the email you used at checkout:</label>
                  <Input 
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-6 py-4 text-lg rounded-lg border-[#E5DFD0]"
                    disabled={isLoading}
                    data-testid="access-email-input"
                  />
                </div>
                
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-4 text-lg rounded-lg transition-all"
                  data-testid="access-find-btn"
                >
                  {isLoading ? 'Looking up...' : 'Find My Purchases'}
                </Button>
              </form>
              
              {/* Downloads Section */}
              {looked && downloads && downloads.length > 0 && (
                <div className="mt-8 pt-8 border-t border-[#E5DFD0]" data-testid="downloads-section">
                  <h3 className="text-xl font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                    <Download className="w-5 h-5 text-[#6B7A5C]" />
                    Your PDF Downloads
                  </h3>
                  <div className="space-y-3">
                    {downloads.map((dl, index) => (
                      <div 
                        key={index} 
                        className="bg-[#EBE6D8] p-5 rounded-lg flex items-center justify-between"
                        data-testid={`download-item-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[#C17855] bg-opacity-15 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-[#C17855]" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-[#2C1810]">{dl.product_name}</h4>
                            <p className="text-sm text-[#5A5046]">{dl.filename}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDownload(dl.product_id)}
                          className="bg-[#C17855] hover:bg-[#B06745] text-white px-5 py-2 rounded-lg flex items-center gap-2 text-sm flex-shrink-0"
                          data-testid={`download-btn-${index}`}
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Purchases History */}
              {looked && purchases && purchases.length > 0 && (
                <div className="mt-8 pt-8 border-t border-[#E5DFD0]" data-testid="purchases-list">
                  <h3 className="text-xl font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#6B7A5C]" />
                    Purchase History
                  </h3>
                  <div className="space-y-3">
                    {purchases.map((purchase, index) => (
                      <div key={index} className="bg-[#F5F1E8] p-4 rounded-lg" data-testid={`purchase-item-${index}`}>
                        <div className="flex justify-between items-start">
                          <h4 className="font-semibold text-[#2C1810] text-sm">{purchase.product_name}</h4>
                          <span className="text-[#6B7A5C] font-bold text-sm">${purchase.amount_paid?.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#5A5046] mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(purchase.purchased_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* No purchases found */}
              {looked && purchases && purchases.length === 0 && (
                <div className="mt-8 pt-8 border-t border-[#E5DFD0] text-center" data-testid="no-purchases-msg">
                  <p className="text-[#5A5046] mb-2">No purchases found for <strong>{email}</strong></p>
                  <p className="text-[#5A5046] text-sm mb-6">Make sure you're using the same email you used at checkout.</p>
                </div>
              )}
              
              <div className="mt-8 pt-8 border-t border-[#E5DFD0] text-center">
                <p className="text-[#5A5046] mb-4 text-sm">
                  Don't have a guide yet?
                </p>
                <Link to="/products/guide">
                  <Button 
                    variant="outline"
                    className="border-2 border-[#C17855] text-[#C17855] hover:bg-[#C17855] hover:text-white px-6 py-3 rounded-lg transition-all flex items-center gap-2 mx-auto"
                    data-testid="access-get-spendless-btn"
                  >
                    Get SpendLess — $9.99 (Launch Price)
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </main>
        
        <Footer />
      </div>
      <Toaster />
    </>
  );
};

export default AccessPage;
